'use client'

import { signIn } from '@repo/auth/client'
import { Loader2 } from 'lucide-react'
import { useState } from 'react'

import { GoogleMark } from '@/components/atoms/google-mark'
import { useToast } from '@/components/providers/ToastProvider'

/**
 * Social sign-in, one button per provider.
 *
 * A provider is LIVE only when the server says its keys are present
 * (`CONFIGURED_SOCIAL_PROVIDERS`, passed down as `enabled`); otherwise pressing
 * it says so instead of starting an OAuth round-trip that was never going to
 * work. Bouncing somebody out to a provider and returning them with an error is
 * worse than not offering it.
 *
 * That flag is the whole switch: adding `GOOGLE_CLIENT_ID` / `GOOGLE_CLIENT_SECRET`
 * to the project turns this button on with no code change.
 *
 * ── GOOGLE IS THE ONLY ONE ───────────────────────────────────────────────
 * Apple and Facebook used to sit under it. Neither was ever wired: `server.ts`
 * builds `CONFIGURED_SOCIAL_PROVIDERS` from the Google keys alone, so no
 * environment variable existed that could have turned either of them on. They
 * were three buttons offering one thing — and the two that could never work
 * were the two nobody could tell apart from the one that will.
 *
 * ── NOTHING ON SCREEN SAYS "COMING SOON" ANY MORE ────────────────────────
 * A "wkrótce" badge on the button and a caption under it both went, by the
 * owner's decision: the screen is meant to look finished. The refusal survives
 * where it costs nobody a second look — `notWired`, the toast on press. Until
 * the two keys are set, this is a button that looks ready and says otherwise
 * when used. Setting them removes the gap.
 */
const PROVIDERS = [{ id: 'google', label: 'Google', Icon: GoogleMark }] as const

export function SocialAuthButtons({
  verb = 'Kontynuuj',
  enabled = [],
  callbackURL = '/pl/panel',
}: {
  verb?: string
  /** Provider ids the server has keys for. Everything else stays provisional. */
  enabled?: readonly string[]
  /** Where the provider returns the visitor once they are signed in. */
  callbackURL?: string
}) {
  const { toast } = useToast()
  const [pending, setPending] = useState<string | null>(null)

  const notWired = (provider: string) =>
    toast({
      kind: 'info',
      title: `Logowanie przez ${provider} jeszcze nie działa`,
      description: 'Podepniemy je wkrótce. Na razie użyj formularza.',
    })

  const start = async (provider: string, label: string) => {
    setPending(provider)
    try {
      // Redirects away on success, so there is no success branch to write; the
      // callback lands back on `/api/auth/callback/<provider>`, which better-auth
      // owns. Only a refusal returns here.
      const { error } = await signIn.social({ provider, callbackURL })
      if (error) {
        toast({
          kind: 'error',
          title: `Nie udało się zalogować przez ${label}`,
          description: 'Spróbuj ponownie lub użyj formularza poniżej.',
        })
        setPending(null)
      }
    } catch {
      toast({
        kind: 'error',
        title: `Nie udało się zalogować przez ${label}`,
        description: 'Sprawdź połączenie i spróbuj ponownie.',
      })
      setPending(null)
    }
  }

  return (
    <div className="flex flex-col gap-2.5">
      {PROVIDERS.map(({ id, label, Icon }) => {
        const live = enabled.includes(id)
        return (
          <button
            key={id}
            type="button"
            disabled={pending !== null}
            onClick={() => (live ? void start(id, label) : notWired(label))}
            className="inline-flex w-full items-center justify-center gap-2.5 rounded-xl border border-white/12 bg-white/[0.06] py-3 text-[0.95rem] font-semibold text-white/85 transition-colors hover:border-white/30 hover:bg-white/[0.10] hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
          >
            {pending === id ? (
              <Loader2 aria-hidden="true" className="size-4 animate-spin" />
            ) : (
              <Icon />
            )}
            {verb} z {label}
          </button>
        )
      })}
    </div>
  )
}
